import { useState } from "react";

import { reactLocalStorage } from "reactjs-localstorage";

import { canAccessClientStorageWrapper } from "./embed";

export const CONSENT_MODE_KEY = "consentMode" as const;
export const STAFF_VOLUNTEER_HAS_ALREADY_SCANNED_A_TICKET_KEY = "staffVolunteerHasAlreadyScannedATicket" as const;

type LocalStorageKey =
  | "orderTableFilters"
  | "lastUserViews"
  | typeof CONSENT_MODE_KEY
  | "hasPreviewForm"
  | "peer-to-peer-form-data"
  | "googleSSOSignUpData"
  | "googleSSOCompleteGhostSignupData"
  | "zeffyAdminUser"
  | "page-has-been-force-refreshed"
  | "swiftAidConnectionSkipped"
  | "bypassDashboardUnsupportedBrowser"
  | "bypassFormsUnsupportedBrowser"
  | typeof STAFF_VOLUNTEER_HAS_ALREADY_SCANNED_A_TICKET_KEY
  | "dashboardOrganizationSidebarExpandedMenuItem"
  | "paymentsFiltersInfoboxDismissed"
  | "isConnectedOnDashboard";

export const removeFromLocalStorage = canAccessClientStorageWrapper(
  () => (key: LocalStorageKey) => reactLocalStorage.remove(key)
);

export const getFromLocalStorage = canAccessClientStorageWrapper(
  () =>
    <T = string | number | boolean>(key: LocalStorageKey, defaultValue?: T, silent?: boolean) =>
      reactLocalStorage.get<T>(key, defaultValue, silent)
);

export const getObjectFromLocalStorage = canAccessClientStorageWrapper(
  () =>
    <T extends object>(key: LocalStorageKey, defaultValue?: object, silent?: boolean) => {
      const res = reactLocalStorage.getObject(key, defaultValue, silent) as T | undefined;
      if (!res || Object.keys(res).length === 0) {
        return undefined;
      }
      return res;
    }
);

export const setInLocalStorage = canAccessClientStorageWrapper(
  () => (key: LocalStorageKey, value: string | number | boolean) => reactLocalStorage.set(key, value)
);

export const setObjectInLocalStorage = canAccessClientStorageWrapper(
  () => (key: LocalStorageKey, value: object) => reactLocalStorage.setObject(key, value)
);

export const useLocalStorage = <T = string | number | boolean>(
  key: LocalStorageKey,
  defaultValue: T = undefined as T
): [T, (value: T) => void] => {
  const [value, setValue] = useState<T>(getFromLocalStorage?.(key, defaultValue) ?? defaultValue);

  const onChange = (value: T) => {
    setValue(value);
    setInLocalStorage?.(key, value as string | number | boolean);
  };

  return [value, onChange];
};
